import { type FC, useState, useCallback, useMemo, useEffect } from 'react';
import { Empty, Modal, Radio, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import startCase from 'lodash/startCase';
import upperCase from 'lodash/upperCase';
import SearchBox from 'components/molecules/SearchBox';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import type { ImportEventTableColumnConfig } from '../../../config/importEventFromPlanConfig';
import useImportablePlans from '../hooks/queries/useImportablePlans';
import useImportableEvents from '../hooks/queries/useImportableEvents';
import type { ImportableEventRow } from '../hooks/queries/useImportableEvents';
import useImportEventsMutation from '../hooks/mutations/useImportEventsMutation';

type SourceCategory = 'self' | 'parent' | 'child';

/** Traverses a dot-notation path on a row object and returns the string value. */
const getNestedValue = (row: ImportableEventRow, dotPath: string): string => {
    const parts = dotPath.split('.');
    let val: unknown = row;
    for (const part of parts) {
        if (val == null || typeof val !== 'object') return '';
        val = (val as Record<string, unknown>)[part];
    }
    return String(val ?? '');
};

const buildColumns = (cols: ImportEventTableColumnConfig[]): ColumnsType<ImportableEventRow> =>
    cols.map((col) => {
        const antdDataIndex: string | string[] = col.dataIndex.includes('.')
            ? col.dataIndex.split('.')
            : col.dataIndex;

        let render: ((val: unknown) => string) | undefined;
        if (col.renderAs === 'startCase') {
            render = (val: unknown) => startCase(String(val ?? '')) || '-';
        } else if (col.renderAs === 'upperCase') {
            render = (val: unknown) => upperCase(String(val ?? '')) || '-';
        } else if (col.renderAs === 'date') {
            render = (val: unknown) => (val ? dayjs(String(val)).format('MM/DD/YYYY') : '-');
        }

        const sorter =
            col.sortBy === 'date'
                ? (a: ImportableEventRow, b: ImportableEventRow) =>
                      dayjs(getNestedValue(a, col.dataIndex)).unix() -
                      dayjs(getNestedValue(b, col.dataIndex)).unix()
                : (a: ImportableEventRow, b: ImportableEventRow) =>
                      getNestedValue(a, col.dataIndex).localeCompare(getNestedValue(b, col.dataIndex));

        return {
            key: col.key,
            title: col.title,
            dataIndex: antdDataIndex,
            ...(col.width !== undefined && { width: col.width }),
            ellipsis: col.ellipsis ?? true,
            ...(render !== undefined && { render }),
            sorter,
            sortDirections: (col.sortDirections ?? ['descend', 'ascend']) as SortOrder[],
        };
    });

interface Props {
    open: boolean;
    onClose: () => void;
    targetPlanId: string;
}

const ImportEventFromPlanModal: FC<Props> = ({ open, onClose, targetPlanId }) => {
    const { importEventFromPlanConfig } = useTpoGenConfig();
    const { modalTitle, modalWidth = 1200, sourceLabels, tableColumns: columnConfigs } = importEventFromPlanConfig;

    const [selectedSource, setSelectedSource] = useState<SourceCategory>('self');
    const [sourcePlanId, setSourcePlanId] = useState<string | null>(null);
    const [planSearch, setPlanSearch] = useState('');
    const [eventSearch, setEventSearch] = useState('');
    const [eventSearchKeyword, setEventSearchKeyword] = useState('');
    const [selectedPromoIds, setSelectedPromoIds] = useState<string[]>([]);

    const resetState = useCallback(() => {
        setSelectedSource('self');
        setSourcePlanId(null);
        setPlanSearch('');
        setEventSearch('');
        setEventSearchKeyword('');
        setSelectedPromoIds([]);
    }, []);

    useEffect(() => {
        if (!open) resetState();
    }, [open, resetState]);

    const {
        plansByCategory,
        isPending: plansPending,
        isFetchingNextPage: plansFetchingNextPage,
        hasNextPage: plansHasNextPage,
        fetchNextPage: plansFetchNextPage,
    } = useImportablePlans({ planId: targetPlanId, searchString: planSearch });

    const {
        events,
        isPending: eventsPending,
        isFetching: eventsFetching,
        isFetchingNextPage: eventsFetchingNextPage,
        hasNextPage: eventsHasNextPage,
        fetchNextPage: eventsFetchNextPage,
    } = useImportableEvents({ sourcePlanId, targetPlanId, searchString: eventSearchKeyword });

    const { mutate: importEvents, isPending: importPending } = useImportEventsMutation({
        onSuccess: () => onClose(),
    });

    const handleSourceChange = useCallback((category: SourceCategory) => {
        setSelectedSource(category);
        setSourcePlanId(null);
        setSelectedPromoIds([]);
        setEventSearch('');
        setEventSearchKeyword('');
    }, []);

    const handlePlanChange = useCallback((planId: string) => {
        setSourcePlanId(planId);
        setSelectedPromoIds([]);
        setEventSearch('');
        setEventSearchKeyword('');
    }, []);

    const handleImport = useCallback(() => {
        if (!sourcePlanId || selectedPromoIds.length === 0) return;
        importEvents({
            sourcePlanId,
            targetPlanId,
            promoIds: selectedPromoIds,
        });
    }, [sourcePlanId, targetPlanId, selectedPromoIds, importEvents]);

    const availableCategories = useMemo<SourceCategory[]>(() => {
        const cats: SourceCategory[] = ['self'];
        if (plansByCategory.parent.length > 0) cats.push('parent');
        if (plansByCategory.child.length > 0) cats.push('child');
        return cats;
    }, [plansByCategory]);

    const currentPlanOptions = useMemo(
        () =>
            plansByCategory[selectedSource].map((p) => ({
                label: p.plan_name,
                value: p.plan_id,
            })),
        [plansByCategory, selectedSource],
    );

    const tableLocale = useMemo(() => {
        if (!sourcePlanId) {
            return { emptyText: <Empty description="Select a plan to view events" /> };
        }
        return undefined;
    }, [sourcePlanId]);

    const tableColumns = useMemo(() => buildColumns(columnConfigs), [columnConfigs]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleImport}
            okText="Import"
            okButtonProps={{
                disabled: selectedPromoIds.length === 0,
                loading: importPending,
                style: { borderRadius: 6 },
            }}
            cancelButtonProps={{
                disabled: importPending,
                style: { borderColor: '#44338a', color: '#44338a', borderRadius: 6 },
            }}
            width={modalWidth}
            title={modalTitle}
            destroyOnClose
            maskClosable={!importPending}
            closable={!importPending}
        >
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-[#18181b]">Import from:</span>
                        <Radio.Group value={selectedSource} onChange={(e) => handleSourceChange(e.target.value)}>
                            {availableCategories.map((cat) => (
                                <Radio key={cat} value={cat}>
                                    {sourceLabels[cat] ?? cat}
                                    {cat !== 'self' ? ':' : ''}
                                </Radio>
                            ))}
                        </Radio.Group>
                        <SelectWithInfiniteScroller
                            style={{ width: 160 }}
                            placeholder="Select Plan"
                            value={sourcePlanId ?? undefined}
                            options={currentPlanOptions}
                            onChange={handlePlanChange}
                            loading={plansPending || plansFetchingNextPage}
                            hasMore={plansHasNextPage}
                            onInfiniteScrollOnNextMemoised={plansFetchNextPage}
                            showSearch
                            filterOption={false}
                            onSearch={setPlanSearch}
                            notFoundContent={
                                plansPending ? (
                                    <div className="flex justify-center p-3">
                                        <Spin size="small" />
                                    </div>
                                ) : (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plans" />
                                )
                            }
                        />
                    </div>
                    <SearchBox
                        search={eventSearch}
                        setSearch={setEventSearch}
                        onSearch={() => setEventSearchKeyword(eventSearch)}
                    />
                </div>

                <div className="pb-3">
                    <InfiniteScroller
                        data={events}
                        onNext={eventsFetchNextPage}
                        hasMore={eventsHasNextPage}
                        loading={eventsFetchingNextPage}
                        height={378}
                    >
                        <Table<ImportableEventRow>
                            columns={tableColumns}
                            dataSource={events}
                            rowKey="promo_id"
                            pagination={false}
                            loading={eventsPending || eventsFetching}
                            bordered
                            size="middle"
                            locale={tableLocale}
                            rowSelection={{
                                type: 'checkbox',
                                selectedRowKeys: selectedPromoIds,
                                preserveSelectedRowKeys: true,
                                onChange: (keys) => setSelectedPromoIds(keys as string[]),
                            }}
                        />
                    </InfiniteScroller>
                </div>
            </div>
        </Modal>
    );
};

ImportEventFromPlanModal.displayName = 'ImportEventFromPlanModal';
export default ImportEventFromPlanModal;
